<template>
  <div class="md-layout product-prices-page">
    <div class="md-layout-item">
      <md-card class="main-card">
        <!-- Header -->
        <md-card-header class="card-header">
          <div class="header-left">
            <div class="card-icon"><md-icon>currency_pound</md-icon></div>
            <div>
              <h4 class="title">Product Prices</h4>
              <p class="product-name" v-if="product">{{ product.name }}</p>
            </div>
          </div>
          <md-button
            class="md-raised back-btn"
            @click="$router.push('/kid/wingio-products')"
          >
            <md-icon>arrow_back</md-icon> Back
          </md-button>
        </md-card-header>

        <md-card-content>
          <div v-if="loading" class="state-box">
            <md-progress-spinner
              md-mode="indeterminate"
              :md-diameter="36"
              :md-stroke="3"
            />
            <p>Loading...</p>
          </div>

          <div v-else>
            <!-- Marketplace Filter Tabs -->
            <div class="market-tabs" v-if="marketplaceCodes.length > 1">
              <button
                v-for="code in marketplaceCodes"
                :key="code"
                class="market-tab"
                :class="{ active: activeMarket === code }"
                @click="activeMarket = code"
              >
                {{ marketFlag(code) }} {{ code }}
                <span class="tab-count">{{
                  getMarketCount(code)
                }}</span>
              </button>
            </div>

            <!-- Prices Table -->
            <div class="prices-section">
              <div class="prices-table-wrap">
                <table class="prices-table" v-if="filteredPrices.length > 0">
                  <thead>
                    <tr>
                      <th>Market</th>
                      <th>Price</th>
                      <th>Offer Price</th>
                      <th>Offer Label</th>
                      <th>Stripe Price ID</th>
                      <th>Created</th>
                      <th>Default</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="item in filteredPrices"
                      :key="item._id"
                      class="price-row"
                      :class="{ 'row-default': item.isDefault }"
                    >
                      <td>
                        <span class="market-badge">{{
                          item.marketplaceCode || "GB"
                        }}</span>
                      </td>
                      <td>
                        <strong class="price-val">
                          {{ getCurrencySymbol(getItemCurrency(item)) }}{{ item.price }}
                        </strong>
                      </td>
                      <td>
                        <span v-if="item.offerPrice" class="offer-price-val">
                          {{ getCurrencySymbol(getItemCurrency(item)) }}{{ item.offerPrice }}
                        </span>
                        <span v-else class="text-muted">-</span>
                      </td>
                      <td class="text-muted small">
                        {{ item.offerPriceText || "-" }}
                      </td>
                      <td>
                        <code class="stripe-id">{{ item.stripePriceId }}</code>
                      </td>
                      <td class="text-muted small">
                        {{ formatDate(item.created_at) }}
                      </td>
                      <td>
                        <span v-if="item.isDefault" class="default-badge">
                          ✓ Default
                        </span>
                        <button
                          v-else
                          class="set-default-btn"
                          @click="setDefault(item)"
                          title="Set as default"
                        >
                          Set Default
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
                <div v-else class="state-box">
                  <md-icon class="empty-icon">price_change</md-icon>
                  <p>No prices for this market yet</p>
                </div>
              </div>
            </div>

            <!-- Add / Reuse Price Panel -->
            <div class="add-price-panel">
              <div class="panel-header">
                <h5>Add Price</h5>
                <div class="mode-toggle">
                  <button
                    class="mode-btn"
                    :class="{ active: addMode === 'new' }"
                    @click="addMode = 'new'"
                  >
                    <md-icon>add</md-icon> New Price
                  </button>
                  <button
                    class="mode-btn"
                    :class="{ active: addMode === 'reuse' }"
                    @click="switchToReuse"
                  >
                    <md-icon>recycling</md-icon> Reuse Existing
                  </button>
                </div>
              </div>

              <!-- NEW PRICE -->
              <div v-if="addMode === 'new'" class="add-price-form">
                <div class="form-row">
                  <div class="form-group">
                    <label class="field-label">
                      Marketplace <span class="req">*</span>
                    </label>
                    <select
                      v-model="newPrice.marketplaceId"
                      class="field-input field-select"
                      @change="onMarketplaceChange"
                    >
                      <option value="">- select market -</option>
                      <option
                        v-for="m in marketplaces"
                        :key="m._id"
                        :value="m._id"
                      >
                        {{ marketFlag(m.code) }} {{ m.country }} ({{ m.code }})
                      </option>
                    </select>
                  </div>

                  <div
                    class="form-group"
                    v-if="product && product.productType === 'Subscription'"
                  >
                    <label class="field-label">
                      Recurring Interval <span class="req">*</span>
                    </label>
                    <select
                      v-model="newPrice.recurringInterval"
                      class="field-input field-select"
                    >
                      <option value="">- select -</option>
                      <option value="month">Monthly</option>
                      <option value="year">Yearly</option>
                    </select>
                  </div>

                  <div class="form-group">
                    <label class="field-label">
                      Price <span class="req">*</span>
                      <span class="currency-hint">{{ selectedCurrencySymbol }}</span>
                    </label>
                    <div class="input-prefix-wrap">
                      <span class="input-prefix">{{ selectedCurrencySymbol }}</span>
                      <input
                        v-model.number="newPrice.price"
                        type="number"
                        min="0"
                        step="0.01"
                        class="field-input prefix-input"
                        placeholder="0.00"
                      />
                    </div>
                  </div>

                  <div
                    class="form-group"
                    v-if="product && product.productType !== 'Subscription'"
                  >
                    <label class="field-label">Offer Price</label>
                    <div class="input-prefix-wrap">
                      <span class="input-prefix">{{ selectedCurrencySymbol }}</span>
                      <input
                        v-model.number="newPrice.offerPrice"
                        type="number"
                        min="0"
                        step="0.01"
                        class="field-input prefix-input"
                        placeholder="0.00"
                      />
                    </div>
                  </div>

                  <div
                    class="form-group"
                    v-if="product && product.productType !== 'Subscription'"
                  >
                    <label class="field-label">Offer Label</label>
                    <input
                      v-model="newPrice.offerPriceText"
                      class="field-input"
                      placeholder="e.g. Limited offer"
                    />
                  </div>

                  <div class="form-group form-group-check">
                    <label class="field-label">Set as Default</label>
                    <md-checkbox v-model="newPrice.isDefault">
                      Default for this market
                    </md-checkbox>
                  </div>
                </div>

                <div v-if="duplicateWarning" class="warning-banner">
                  <md-icon>warning</md-icon>
                  A price of {{ selectedCurrencySymbol }}{{ newPrice.price }} already
                  exists for {{ newPrice.marketplaceCode || "this market" }}. The
                  existing Stripe price will be reused - no duplicate will be created.
                </div>

                <div class="form-actions">
                  <button class="btn-add" @click="addNewPrice" :disabled="adding">
                    <i v-if="adding" class="fa fa-spinner fa-spin"></i>
                    <md-icon v-else>add</md-icon>
                    {{ adding ? "Adding..." : "Add Price" }}
                  </button>
                </div>
              </div>

              <!-- REUSE EXISTING -->
              <div v-if="addMode === 'reuse'" class="reuse-section">
                <p class="reuse-desc">
                  Select a previously created price to reuse its Stripe Price ID
                  - no new charge object will be created.
                </p>

                <div v-if="loadingAllPrices" class="state-box small-state">
                  <md-progress-spinner
                    md-mode="indeterminate"
                    :md-diameter="24"
                    :md-stroke="2"
                  />
                  <span>Loading...</span>
                </div>

                <div v-else-if="allPrices.length === 0" class="state-box small-state">
                  <p>No past prices found.</p>
                </div>

                <div v-else class="reuse-list">
                  <div
                    v-for="p in allPrices"
                    :key="p._id"
                    class="reuse-row"
                    :class="{ selected: reuseSelected === p._id }"
                    @click="reuseSelected = p._id"
                  >
                    <div class="reuse-radio">
                      <md-icon>{{
                        reuseSelected === p._id
                          ? "radio_button_checked"
                          : "radio_button_unchecked"
                      }}</md-icon>
                    </div>
                    <div class="reuse-info">
                      <span class="market-badge">{{ p.marketplaceCode || "GB" }}</span>
                      <strong>{{ getCurrencySymbol(getItemCurrency(p)) }}{{ p.price }}</strong>
                      <span v-if="p.offerPrice" class="offer-tag">
                        Offer: {{ getCurrencySymbol(getItemCurrency(p)) }}{{ p.offerPrice }}
                      </span>
                      <code class="stripe-id-small">{{ p.stripePriceId }}</code>
                    </div>
                    <span class="reuse-date">{{ formatDate(p.created_at) }}</span>
                  </div>
                </div>

                <div class="form-group form-group-check" v-if="reuseSelected">
                  <md-checkbox v-model="reuseAsDefault">
                    Set as Default for its market
                  </md-checkbox>
                </div>

                <div class="form-actions">
                  <button
                    class="btn-add"
                    @click="reusePrice"
                    :disabled="!reuseSelected || adding"
                  >
                    <i v-if="adding" class="fa fa-spinner fa-spin"></i>
                    <md-icon v-else>recycling</md-icon>
                    {{ adding ? "Setting..." : "Use This Price" }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </md-card-content>
      </md-card>
    </div>
  </div>
</template>

<script>
import ProductService from "../../services/SuperAdmin/Kids/ProductService";

export default {
  data() {
    return {
      loading: false,
      adding: false,
      loadingAllPrices: false,
      productId: this.$route.query.id,
      product: null,
      prices: [],
      allPrices: [],
      marketplaces: [],
      activeMarket: "ALL",
      addMode: "new",
      reuseSelected: null,
      reuseAsDefault: false,
      newPrice: this.defaultNewPrice(),
    };
  },
  computed: {
    marketplaceCodes() {
      const codes = [
        ...new Set(this.prices.map((p) => p.marketplaceCode || "GB")),
      ];
      return ["ALL", ...codes];
    },
    pricesByMarket() {
      const map = {};
      this.prices.forEach((p) => {
        const code = p.marketplaceCode || "GB";
        if (!map[code]) map[code] = [];
        map[code].push(p);
      });
      return map;
    },
    filteredPrices() {
      if (this.activeMarket === "ALL") return this.prices;
      return this.prices.filter(
        (p) => (p.marketplaceCode || "GB") === this.activeMarket
      );
    },
    selectedCurrencySymbol() {
      const m = this.marketplaces.find(
        (x) => x._id === this.newPrice.marketplaceId
      );
      if (!m) return "£";
      if (m.currencyId && m.currencyId.symbol) return m.currencyId.symbol;
      return "£";
    },
    duplicateWarning() {
      if (!this.newPrice.price || !this.newPrice.marketplaceId) return false;
      const market = this.marketplaces.find(
        (m) => m._id === this.newPrice.marketplaceId
      );
      const code = market ? market.code : null;
      if (!code) return false;
      return this.prices.some(
        (p) =>
          p.price == this.newPrice.price &&
          (p.marketplaceCode || "GB") === code
      );
    },
  },
  methods: {
    defaultNewPrice() {
      return {
        marketplaceId: "",
        marketplaceCode: "",
        price: null,
        offerPrice: null,
        offerPriceText: "",
        isDefault: false,
        recurringInterval: "",
      };
    },
    // Replaces optional chaining item.currency || item.stripePriceDetail?.currency
    getItemCurrency(item) {
      if (item.currency) return item.currency;
      if (item.stripePriceDetail && item.stripePriceDetail.currency) {
        return item.stripePriceDetail.currency;
      }
      return "GBP";
    },
    // Replaces pricesByMarket[code]?.length
    getMarketCount(code) {
      if (code === "ALL") return this.prices.length;
      return this.pricesByMarket[code] ? this.pricesByMarket[code].length : 0;
    },
    getCurrencySymbol(currency) {
      const map = {
        usd: "$",
        eur: "€",
        gbp: "£",
        inr: "₹",
        jpy: "¥",
        aud: "A$",
        cad: "C$",
      };
      return (
        map[(currency || "").toLowerCase()] || (currency || "").toUpperCase()
      );
    },
    formatDate(date) {
      if (!date) return "-";
      return new Date(date).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },
    marketFlag(code) {
      try {
        return code
          .toUpperCase()
          .replace(/./g, (c) =>
            String.fromCodePoint(c.charCodeAt(0) + 127397)
          );
      } catch (_) {
        return "🌍";
      }
    },
    switchToReuse() {
      this.addMode = "reuse";
      this.loadAllPrices();
    },
    onMarketplaceChange() {
      const m = this.marketplaces.find(
        (x) => x._id === this.newPrice.marketplaceId
      );
      if (m) this.newPrice.marketplaceCode = m.code;
    },
    loadData() {
      this.loading = true;
      Promise.all([
        ProductService.getProductPrices({ id: this.productId }),
        ProductService.marketplaceList(),
      ])
        .then(([pricesRes, mpRes]) => {
          this.prices = pricesRes.data.data;
          this.product = pricesRes.data.produtDetail;
          this.marketplaces = mpRes.data.data.filter((m) => m.isActive);
        })
        .catch((e) => {
          this.$toast.error(
            (e.response && e.response.data && e.response.data.respones_msg) ||
              "Failed to load."
          );
        })
        .finally(() => {
          this.loading = false;
        });
    },
    loadAllPrices() {
      this.loadingAllPrices = true;
      ProductService.getProductPrices({ id: this.productId })
        .then((r) => {
          this.allPrices = r.data.data;
        })
        .catch(() => {})
        .finally(() => {
          this.loadingAllPrices = false;
        });
    },
    async addNewPrice() {
      if (!this.newPrice.marketplaceId) {
        this.$toast.error("Please select a marketplace.");
        return;
      }
      if (!this.newPrice.price || this.newPrice.price <= 0) {
        this.$toast.error("Enter a valid price.");
        return;
      }
      if (
        this.product &&
        this.product.productType === "Subscription" &&
        !this.newPrice.recurringInterval
      ) {
        this.$toast.error("Please select a recurring interval.");
        return;
      }

      const selectedMarket = this.marketplaces.find(
        (m) => m._id === this.newPrice.marketplaceId
      );
      const marketCode =
        selectedMarket ? selectedMarket.code : "GB";
      const marketCurrency =
        selectedMarket && selectedMarket.currencyId && selectedMarket.currencyId.code
          ? selectedMarket.currencyId.code
          : "GBP";

      this.adding = true;
      ProductService.addProductPrice({
        productdId: this.productId,
        price: this.newPrice.price,
        offerPrice: this.newPrice.offerPrice,
        offerPriceText: this.newPrice.offerPriceText,
        isDefault: this.newPrice.isDefault,
        marketplaceId: this.newPrice.marketplaceId,
        marketplaceCode: marketCode,
        currency: marketCurrency,
        recurringInterval: this.newPrice.recurringInterval,
      })
        .then((r) => {
          const msg =
            r.data.reusedStripePrice
              ? "Price added - reused existing Stripe price."
              : "Price created successfully.";
          this.$toast.success(msg);
          this.newPrice = this.defaultNewPrice();
          this.loadData();
        })
        .catch((e) => {
          this.$toast.error(
            (e.response && e.response.data && e.response.data.respones_msg) ||
              "Failed to add price."
          );
        })
        .finally(() => {
          this.adding = false;
        });
    },
    reusePrice() {
      if (!this.reuseSelected) return;
      this.adding = true;
      ProductService.setDefaultPrice({ productdPriceId: this.reuseSelected })
        .then(() => {
          this.$toast.success("Price set as default.");
          this.reuseSelected = null;
          this.loadData();
        })
        .catch((e) => {
          this.$toast.error(
            (e.response && e.response.data && e.response.data.respones_msg) ||
              "Failed."
          );
        })
        .finally(() => {
          this.adding = false;
        });
    },
    setDefault(item) {
      ProductService.setDefaultPrice({ productdPriceId: item._id })
        .then(() => {
          this.$toast.success("Default price updated.");
          this.loadData();
        })
        .catch((e) => {
          this.$toast.error(
            (e.response && e.response.data && e.response.data.respones_msg) ||
              "Failed."
          );
        });
    },
  },
  beforeMount() {
    this.loadData();
  },
};
</script>

<style scoped>
.product-prices-page {
  font-family: "Segoe UI", sans-serif;
}
.main-card {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}
.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px 16px;
  background: linear-gradient(135deg, #00897b 0%, #004d40 100%);
  color: white;
}
.header-left {
  display: flex;
  align-items: center;
  gap: 14px;
}
.card-icon {
  width: 44px;
  height: 44px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.card-icon .md-icon {
  color: white !important;
}
.title {
  color: white;
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}
.product-name {
  color: rgba(255, 255, 255, 0.8);
  margin: 2px 0 0;
  font-size: 13px;
}
.back-btn {
  background: rgba(255, 255, 255, 0.18) !important;
  color: white !important;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.3);
}
.market-tabs {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  padding: 16px 24px 0;
}
.market-tab {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 20px;
  border: 1.5px solid #e0e0e0;
  background: white;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  color: #555;
  transition: all 0.15s;
}
.market-tab:hover {
  border-color: #00897b;
  color: #00897b;
}
.market-tab.active {
  background: #00897b;
  color: white;
  border-color: #00897b;
}
.tab-count {
  background: rgba(255, 255, 255, 0.3);
  border-radius: 10px;
  padding: 1px 7px;
  font-size: 11px;
}
.market-tab:not(.active) .tab-count {
  background: #f0f0f0;
  color: #888;
}
.prices-section {
  padding: 16px 24px;
}
.prices-table {
  width: 100%;
  border-collapse: collapse;
}
.prices-table thead tr {
  background: #f5f5f5;
}
.prices-table th {
  padding: 10px 14px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #666;
  border-bottom: 2px solid #e0e0e0;
}
.prices-table td {
  padding: 10px 14px;
  font-size: 14px;
  border-bottom: 1px solid #f0f0f0;
  vertical-align: middle;
}
.price-row:hover td {
  background: #f5fffe;
}
.row-default td {
  background: #e8f5e9 !important;
}
.text-muted {
  color: #888;
}
.small {
  font-size: 12px;
}
.market-badge {
  background: #e0f2f1;
  color: #00695c;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700;
  font-family: monospace;
}
.price-val {
  font-size: 15px;
  color: #333;
}
.offer-price-val {
  color: #e53935;
  font-weight: 600;
}
.stripe-id {
  font-family: monospace;
  font-size: 11px;
  color: #888;
  background: #f5f5f5;
  padding: 2px 6px;
  border-radius: 4px;
}
.default-badge {
  background: #00897b;
  color: white;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
}
.set-default-btn {
  background: none;
  border: 1px dashed #00897b;
  color: #00897b;
  border-radius: 8px;
  padding: 3px 10px;
  font-size: 11px;
  cursor: pointer;
  font-weight: 600;
}
.set-default-btn:hover {
  background: #e0f2f1;
}
.add-price-panel {
  margin: 0 24px 24px;
  border: 1.5px solid #e0e0e0;
  border-radius: 12px;
  overflow: hidden;
}
.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  background: #f8f9fa;
  border-bottom: 1px solid #e0e0e0;
}
.panel-header h5 {
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: #333;
}
.mode-toggle {
  display: flex;
  border: 1.5px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
}
.mode-btn {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 6px 14px;
  border: none;
  background: white;
  color: #666;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
}
.mode-btn.active {
  background: #00897b;
  color: white;
}
.mode-btn .md-icon {
  font-size: 16px !important;
  color: inherit !important;
}
.add-price-form,
.reuse-section {
  padding: 18px;
}
.form-row {
  display: flex;
  gap: 14px;
  flex-wrap: wrap;
  align-items: flex-end;
}
.form-group {
  display: flex;
  flex-direction: column;
}
.form-group-check {
  justify-content: flex-end;
  padding-bottom: 4px;
}
.field-label {
  display: block;
  font-size: 11px;
  font-weight: 700;
  color: #555;
  margin-bottom: 5px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}
.req {
  color: #e53935;
}
.currency-hint {
  color: #888;
  font-weight: 400;
  text-transform: none;
  letter-spacing: 0;
  font-size: 12px;
}
.field-input {
  padding: 9px 12px;
  border: 1.5px solid #e0e0e0;
  border-radius: 8px;
  font-size: 14px;
  color: #333;
  outline: none;
  transition: border-color 0.2s;
  box-sizing: border-box;
  background: white;
}
.field-input:focus {
  border-color: #00897b;
}
.field-select {
  -webkit-appearance: none;
  cursor: pointer;
  min-width: 180px;
}
.input-prefix-wrap {
  position: relative;
}
.input-prefix {
  position: absolute;
  left: 9px;
  top: 50%;
  transform: translateY(-50%);
  color: #888;
  font-size: 14px;
  pointer-events: none;
}
.prefix-input {
  padding-left: 24px;
  width: 100px;
}
.warning-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: #fff8e1;
  border: 1px solid #ffe082;
  border-radius: 8px;
  font-size: 13px;
  color: #f57f17;
  margin-top: 12px;
}
.warning-banner .md-icon {
  color: #f57f17 !important;
  font-size: 18px !important;
}
.form-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 14px;
}
.btn-add {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 20px;
  border-radius: 8px;
  border: none;
  background: #00897b;
  color: white;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
}
.btn-add:hover:not(:disabled) {
  background: #00695c;
}
.btn-add:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}
.btn-add .md-icon {
  color: white !important;
  font-size: 17px !important;
}
.reuse-desc {
  font-size: 13px;
  color: #888;
  margin: 0 0 14px;
}
.reuse-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 280px;
  overflow-y: auto;
  margin-bottom: 12px;
}
.reuse-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  border: 1.5px solid #e0e0e0;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s;
}
.reuse-row:hover {
  border-color: #00897b;
  background: #f0fffe;
}
.reuse-row.selected {
  border-color: #00897b;
  background: #e0f2f1;
}
.reuse-radio .md-icon {
  color: #00897b !important;
  font-size: 20px !important;
}
.reuse-info {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  flex-wrap: wrap;
}
.reuse-info strong {
  font-size: 15px;
  color: #333;
}
.offer-tag {
  font-size: 12px;
  color: #e53935;
}
.stripe-id-small {
  font-family: monospace;
  font-size: 11px;
  color: #888;
  background: #f5f5f5;
  padding: 2px 6px;
  border-radius: 4px;
}
.reuse-date {
  font-size: 11px;
  color: #aaa;
  white-space: nowrap;
}
.state-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: #aaa;
  text-align: center;
}
.small-state {
  padding: 20px;
  flex-direction: row;
  gap: 10px;
  font-size: 14px;
}
.empty-icon {
  font-size: 40px !important;
  color: #ddd !important;
  margin-bottom: 10px;
}
</style>